import cloneDeep from 'lodash.clonedeep';
import { call, debounce, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { EXPANDED_CANVAS_SIZE } from 'js/editor/EditorCanvas/ProjectCanvas';
import { updateBitmapFonts } from 'js/actionCreators/fontActions';
import { loadCursorsSuccess } from 'js/actionCreators/loadCursorsSuccessAction';
import { showElementsPanel, showCallout, showError } from 'js/actionCreators/uiActions';
import { trackSelectAll } from 'js/actionCreators/trackingActions';
import { MAX_SCROLL_MULTIPLIER, canvasSizes } from 'js/config/defaults';
import { getActiveScene } from 'js/shared/helpers/scenesHelpers';
import { rendererLog } from 'js/shared/lib/CloudRenderer';

import { editorSetEditPanelMode, toggleFirstArrowPress } from '../actionCreators/editorActions';
import {
  ADD_SCRIBE_REQUESTED,
  CHANGE_SCENE_ORDER,
  CREATE_ELEMENT,
  CREATE_IMAGE_ELEMENT,
  CREATE_SCENE,
  CREATE_SHAPE_ELEMENT,
  CREATE_TEXT_ELEMENT,
  DELETE_ACTIVE_ELEMENTS,
  DELETE_MULTIPLE_ELEMENTS_BY_ID,
  DELETE_SCRIBE,
  DELETE_SELECTED_SCENES,
  DUPLICATE_SELECTED_SCENES,
  FILTER_SCRIBES_BY_SEARCH_TERM,
  FILTER_SCRIBES_BY_CANVAS_SIZE,
  LOAD_CURSORS,
  LOAD_SCRIBES,
  LOAD_SCRIBES_SUCCESS,
  LOAD_MORE_SCRIBES,
  LOAD_SCRIBE_BY_ID,
  REARRANGE_ELEMENTS_ON_TIMELINE,
  ON_MOVE_WITH_KEYS,
  SELECT_ALL,
  SET_ACTIVE_SCRIBE,
  SORT_SCRIBES,
  UPDATE_SCENE,
  UPDATE_SCENE_ELEMENTS,
  UPDATE_SCENE_THUMBNAIL,
  UPDATE_SCRIBE_FAILED,
  deleteScribeSuccess,
  loadScribes as loadScribesAction,
  loadScribesSuccess,
  loadMoreScribesSuccess,
  setActiveElements,
  setSelectedScenes,
  setSelectedScenes as setSelectedScenesAction,
  updateScribe as updateScribeAction,
  SET_SELECTED_AUDIO_CLIP,
  loadAllProjects as loadAllProjectsAction,
  LOAD_ALL_PROJECTS_SUCCESS,
  loadAllProjectsSuccess as loadAllProjectsSuccessAction,
  LOAD_ALL_PROJECTS
} from '../actionCreators/scribeActions';
import { DEFAULT_PAGE_SIZE } from '../config/config';
import {
  ACCOUNT_TYPES,
  ALLOWED_CURSORS,
  ALLOWED_DRAG_CURSORS,
  ALLOWED_ERASE_CURSORS,
  PROJECT_DELETED_FLASH_TITLE
} from '../config/consts';
import { sendErrorToSentry } from '../logging';
import { appServices } from '../shared/helpers/app-services/AppServices';
import { deleteProject, getProjectList } from '../shared/helpers/app-services/AppServices/projects';

import createNewScene from './sagaHelpers/createNewScene';
import { cursorAllowedListFilter } from './sagaHelpers/cursorAllowedListFilter';
import deleteScenesFromScribe from './sagaHelpers/deleteScenesFromScribe';
import { duplicateScenesIntoScribe } from './sagaHelpers/duplicateScenesIntoScribe';
import multiDragAwareReorder from './sagaHelpers/multiDragAwareReorder';
import duplicateScribeSagas from './scribeSagas/duplicateScribeSagas';
import replaceActiveScene from './sagaHelpers/replaceActiveScene';
import { updateElementsInScene } from './sagaHelpers/updateElementsInScene';
import { getActiveElements, getScribeById, getUserAccountType } from './selectors';
import updateProjectTitleSaga from './updateProjectTitle';
import { createElement } from './scribeSagas/createElement';
import { prepProjectForEditor } from './projectSagas/prepProjectForEditor';
import { loadProjectById } from './projectSagas/loadProjectById';
import { addProject } from './projectSagas/addProject';
import { createTextElement } from './elementSagas/createTextElement';
import { createShapeElement } from './elementSagas/createShapeElement';
import { createImageElement } from './elementSagas/createImageElement';
import { deleteActiveElements } from './elementSagas/deleteActiveElements';
import { deleteMultipleElementsById } from './elementSagas/deleteMultipleElementsById';
import { rearrangeElementsOnTimeline } from './projectSagas/rearrangeElementsOnTimeline';

const createHomepageProject = projectData => ({
  id: projectData.id,
  title: projectData.title,
  createdOn: new Date(projectData.createdDate),
  updatedOn: new Date(projectData.updatedDate ?? projectData.createdDate),
  canvasSize: projectData.canvasSize,
  thumbnailImage: projectData.thumbnail,
  viewOnly: projectData.viewOnly,
  downloadReady: projectData.downloadReady
});

function* loadScribes(action = {}) {
  const userAccountType = yield select(getUserAccountType);
  const { query = {} } = action;
  const { sortBy = 'modifiedDate', sortOrder = 'desc', searchTerm = '' } = query;

  const queryParams = new URLSearchParams({
    sortBy,
    sortOrder,
    trimmedContent: 'true',
    limit: DEFAULT_PAGE_SIZE.toString(),
    offset: '0'
  });

  if (searchTerm) {
    queryParams.set('search', searchTerm);
  }

  try {
    const page = yield call(getProjectList, appServices.exchangeCognitoTokenForSparkolToken, queryParams);
    const projects = page.map(project => {
      if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
        // The view only flag from the API is determined by a user's session and when a user has upgraded the
        // session isn't renewed until they log out and log in again.
        // This ensures when the user upgrades they are able to edit their scribes in their current session
        project.viewOnly = false;
      }
      return createHomepageProject(project);
    });

    const hasMore = projects.length === DEFAULT_PAGE_SIZE;

    // For now, we'll estimate total count. If API provides it in response headers or body, use that instead
    const totalCount = hasMore ? DEFAULT_PAGE_SIZE + 1 : projects.length;

    yield put(loadScribesSuccess(projects, hasMore, totalCount));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* loadMoreScribes() {
  const userAccountType = yield select(getUserAccountType);
  const { scribesSortOrder, projectSearchTerm, currentProjectPage } = yield select(state => state.scribes);

  let sortBy = 'modifiedDate';
  let sortOrder = 'desc';

  switch (scribesSortOrder) {
    case 'titleAscend':
      sortBy = 'title';
      sortOrder = 'asc';
      break;
    case 'titleDescend':
      sortBy = 'title';
      sortOrder = 'desc';
      break;
    default:
      sortBy = 'modifiedDate';
      sortOrder = 'desc';
  }

  const queryParams = new URLSearchParams({
    sortBy,
    sortOrder,
    trimmedContent: 'true',
    limit: DEFAULT_PAGE_SIZE.toString(),
    offset: (currentProjectPage * DEFAULT_PAGE_SIZE).toString()
  });

  if (projectSearchTerm) {
    queryParams.set('search', projectSearchTerm);
  }

  try {
    const page = yield call(getProjectList, appServices.exchangeCognitoTokenForSparkolToken, queryParams);
    const projects = page.map(project => {
      if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
        project.viewOnly = false;
      }
      return createHomepageProject(project);
    });

    const hasMore = projects.length === DEFAULT_PAGE_SIZE;

    yield put(loadMoreScribesSuccess(projects, hasMore));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* loadAllProjects() {
  const userAccountType = yield select(getUserAccountType);
  const { scribesSortOrder, homepageProjects, hasMoreProjects } = yield select(state => state.scribes);

  // If no more projects to load, we're done
  if (!hasMoreProjects) {
    yield put(loadAllProjectsSuccessAction());
    return;
  }

  let sortBy = 'modifiedDate';
  let sortOrder = 'desc';

  switch (scribesSortOrder) {
    case 'titleAscend':
      sortBy = 'title';
      sortOrder = 'asc';
      break;
    case 'titleDescend':
      sortBy = 'title';
      sortOrder = 'desc';
      break;
    default:
      sortBy = 'modifiedDate';
      sortOrder = 'desc';
  }

  try {
    // Load all remaining pages
    let currentOffset = homepageProjects.length;
    let hasMore = hasMoreProjects;

    while (hasMore) {
      const queryParams = new URLSearchParams({
        sortBy,
        sortOrder,
        trimmedContent: 'true',
        limit: DEFAULT_PAGE_SIZE.toString(),
        offset: currentOffset.toString()
      });

      // Don't pass search term when loading all projects for client-side filtering

      const page = yield call(getProjectList, appServices.exchangeCognitoTokenForSparkolToken, queryParams);
      const projects = page.map(project => {
        if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
          project.viewOnly = false;
        }
        return createHomepageProject(project);
      });

      hasMore = projects.length === DEFAULT_PAGE_SIZE;

      // Add projects to state incrementally
      yield put(loadMoreScribesSuccess(projects, hasMore));

      currentOffset += projects.length;
    }

    yield put(loadAllProjectsSuccessAction());
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
    yield put(loadAllProjectsSuccessAction()); // Mark as done even on error
  }
}

function* deleteScribe(action) {
  try {
    yield call(deleteProject, action.scribe, appServices.exchangeCognitoTokenForSparkolToken);
    yield put(deleteScribeSuccess(action.scribe.id, action.scribe.title));
    yield put(loadScribesAction());
    yield put(
      showCallout({
        title: PROJECT_DELETED_FLASH_TITLE,
        details: action.scribe.title
      })
    );
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* sortScribes(action) {
  try {
    const { projectSearchTerm, allProjectsLoaded } = yield select(state => state.scribes);

    // If all projects are already loaded (e.g., when filtering by orientation),
    // just update the sort order in the reducer - the UI will handle client-side sorting
    if (allProjectsLoaded) {
      // Reducer will update scribesSortOrder, and the UI can sort the existing projects client-side
      return;
    }

    // Otherwise, fetch sorted data from the server
    let sortBy = 'title';
    let sortOrder;
    switch (action.sortOrder) {
      case 'titleAscend':
        sortOrder = 'asc';
        break;
      case 'titleDescend':
        sortOrder = 'desc';
        break;
      default:
        sortBy = 'modifiedDate';
        sortOrder = 'desc';
    }
    const query = {
      sortBy,
      sortOrder,
      searchTerm: projectSearchTerm
    };

    yield call(loadScribes, { query });
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* handleOrientationFilter(action) {
  try {
    const { allProjectsLoaded, projectSearchTerm } = yield select(state => state.scribes);

    if (action.size === 'allSizes' && projectSearchTerm) {
      // When switching back to 'allSizes' with a search term, reload filtered projects from server
      // The reducer will have already set allProjectsLoaded to false
      const { scribesSortOrder } = yield select(state => state.scribes);

      let sortBy = 'modifiedDate';
      let sortOrder = 'desc';

      switch (scribesSortOrder) {
        case 'titleAscend':
          sortBy = 'title';
          sortOrder = 'asc';
          break;
        case 'titleDescend':
          sortBy = 'title';
          sortOrder = 'desc';
          break;
        default:
          sortBy = 'modifiedDate';
          sortOrder = 'desc';
      }

      const query = {
        sortBy,
        sortOrder,
        searchTerm: projectSearchTerm
      };

      yield call(loadScribes, { query });
    } else if (action.size !== 'allSizes' && !allProjectsLoaded) {
      // If search term exists, reload all projects first (server-filtered results need to be replaced)
      if (projectSearchTerm) {
        // Use fork to run loadScribes in parallel, allowing take() to catch the dispatched action
        yield fork(loadScribes, {});
        yield take(LOAD_SCRIBES_SUCCESS);
      }

      // Load remaining projects for client-side filtering
      yield put(loadAllProjectsAction());
      yield take(LOAD_ALL_PROJECTS_SUCCESS);
    }
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* handleSearchTermChange(action) {
  try {
    const { scribesSortOrder, allProjectsLoaded } = yield select(state => state.scribes);

    // If all projects are already loaded (e.g., when filtering by orientation),
    // just update the search term in the reducer - the UI will handle client-side filtering
    if (allProjectsLoaded) {
      // Reducer will update projectSearchTerm, and the UI can filter the existing projects client-side
      return;
    }

    // Otherwise, fetch filtered data from the server
    let sortBy = 'modifiedDate';
    let sortOrder = 'desc';

    switch (scribesSortOrder) {
      case 'titleAscend':
        sortBy = 'title';
        sortOrder = 'asc';
        break;
      case 'titleDescend':
        sortBy = 'title';
        sortOrder = 'desc';
        break;
      default:
        sortBy = 'modifiedDate';
        sortOrder = 'desc';
    }

    const query = {
      sortBy,
      sortOrder,
      searchTerm: action.term
    };

    yield call(loadScribes, { query });
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
  }
}

function* loadCursors() {
  rendererLog(`REQUEST: loading cursors (hands)`);
  const cursors = yield call(appServices.getCursors);
  rendererLog(`SUCCESS: loading cursors (hands)`);

  const allowedCursors = cursors.filter(cursor => cursorAllowedListFilter(cursor, ALLOWED_CURSORS));

  const allowedEraseCursors = cursors.filter(cursor => cursorAllowedListFilter(cursor, ALLOWED_ERASE_CURSORS));

  const allowedDragCursors = cursors.filter(cursor => cursorAllowedListFilter(cursor, ALLOWED_DRAG_CURSORS));

  yield put(loadCursorsSuccess({ allowedCursors, allowedEraseCursors, allowedDragCursors }));
}

function* updateSceneElements({ elements, scribeId, thumbnail, sceneId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const updatedScribe = cloneDeep(scribe);
  let newActiveScribe = yield updateElementsInScene(updatedScribe, elements);

  const sceneToUpdate = newActiveScribe.scenes.find(scene => scene.id === sceneId);
  sceneToUpdate.thumbnailImage = thumbnail;
  if (sceneToUpdate === newActiveScribe.scenes[0]) {
    newActiveScribe.thumbnailImage = thumbnail;
  }
  yield put(updateScribeAction(newActiveScribe));
  yield put(updateBitmapFonts(newActiveScribe.elements.filter(element => element.type === 'Text')));
}

function* updateSceneThumbnail({ scribeId, thumbnail }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const updatedScribe = cloneDeep(scribe);
  const skipUndoHistory = true;

  const activeScene = updatedScribe.scenes.find(scene => scene.active);
  activeScene.thumbnailImage = thumbnail;
  if (activeScene === updatedScribe.scenes[0]) {
    updatedScribe.thumbnailImage = thumbnail;
  }
  yield put(updateScribeAction(updatedScribe, skipUndoHistory));
}

function* createScene({ scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const sceneIds = yield select(state => state.scribes.selectedSceneIds);

  const { newActiveScribe, sceneId } = createNewScene(scribe, sceneIds);
  yield put(updateScribeAction(newActiveScribe));

  yield put(setSelectedScenesAction([sceneId]));
}

function* duplicateScenes({ scribeId }) {
  const sceneIds = yield select(state => state.scribes.selectedSceneIds);
  const scribe = yield select(state => getScribeById(state, scribeId));

  const { newActiveScribe, clonedSelectedScenes } = duplicateScenesIntoScribe(scribe, sceneIds);

  yield put(updateScribeAction(newActiveScribe));
  yield put(setSelectedScenesAction(clonedSelectedScenes));
}

function* deleteSelectedScenes({ scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const sceneIdsToDelete = yield select(state => state.scribes.selectedSceneIds);

  const updatedScribe = deleteScenesFromScribe(scribe, sceneIdsToDelete);

  yield put(updateScribeAction(updatedScribe));
  yield put(setSelectedScenesAction());
}

function* updateScene({ scene, scribeId }) {
  const activeScribe = yield select(getScribeById, scribeId);

  const newActiveScribe = replaceActiveScene(scene, activeScribe);

  yield put(updateScribeAction(newActiveScribe));
}

function* changeSceneOrder({ scribeId, source, destination, draggableId }) {
  const scribe = yield select(getScribeById, scribeId);
  const selectedSceneIds = yield select(state => state.scribes.selectedSceneIds);

  const updatedScribe = cloneDeep(scribe);
  const { scenes } = updatedScribe;
  const updatedScenes = multiDragAwareReorder(scenes, source, destination, draggableId, selectedSceneIds);
  updatedScribe.scenes = updatedScenes;
  updatedScribe.thumbnailImage = updatedScenes[0].thumbnailImage;

  yield put(updateScribeAction(updatedScribe));
}

function* selectAll({ scribeId, eventTrigger }) {
  const state = yield select();

  const scribe = getScribeById(state, scribeId);

  const hasScenesSelected = state.scribes.selectedSceneIds.length > 0;

  if (hasScenesSelected) {
    const sceneIds = scribe.scenes.map(({ id }) => id);
    yield put(setSelectedScenes(sceneIds));
  } else {
    const scene = getActiveScene(scribe);
    if (!scene) {
      return;
    }
    const elementIds = scene.elementIds.concat();
    yield put(setActiveElements(elementIds));
  }

  yield put(trackSelectAll({ scribeId, eventTrigger, hasScenesSelected }));
}

function* updateScribeFailed({ error }) {
  yield put(
    showError({
      message: `${error}`,
      description: 'Please wait a moment and try loading your project again'
    })
  );
}

function* audioClipSelected({ selectedAudioClip }) {
  if (!selectedAudioClip) {
    yield put(editorSetEditPanelMode(null));
    return;
  }
  yield put(editorSetEditPanelMode('audio'));
  // yield put(updateScribe({ selectedAudioClipId: selectedAudioClip.assetId }));

  const isElementsPanelOpen = yield select(state => state.ui.showElementsPanel);
  if (isElementsPanelOpen) return;
  yield put(showElementsPanel(true));
}

// eslint-disable-next-line no-unused-vars
let delayedLastArrowCheckCall;

function* moveElementsWithKeys({ scribeId, axis, modifier: modifierParam }) {
  let modifier = modifierParam;
  const scribe = yield select(getScribeById, scribeId);
  const activeElementIds = yield select(getActiveElements);

  if (!scribe || !activeElementIds) return;

  const elementsToUpdate = Array.isArray(activeElementIds) ? activeElementIds : [activeElementIds];
  const newScribe = cloneDeep(scribe);

  const canvasWidth = canvasSizes[scribe.canvasSize].width;
  const canvasHeight = canvasSizes[scribe.canvasSize].height;

  const expandedCanvasSize = EXPANDED_CANVAS_SIZE.width;
  const minCanvas = 0 - expandedCanvasSize * MAX_SCROLL_MULTIPLIER;
  const maxCanvas = expandedCanvasSize + expandedCanvasSize * MAX_SCROLL_MULTIPLIER;

  newScribe.elements.forEach(el => {
    if (elementsToUpdate.includes(el.id)) {
      let { x, y, width, height, scaleX, scaleY } = el;

      if (el.type === 'Camera') {
        height = canvasHeight / el.scale;
        width = canvasWidth / el.scale;
        scaleX = scaleY = 1;

        if (axis === 'x') {
          if (x + modifier < minCanvas) {
            modifier = minCanvas;
          } else if (x + width * scaleX + modifier > maxCanvas) {
            modifier = maxCanvas - width * scaleX - x;
          }
        } else if (axis === 'y') {
          if (y + modifier < minCanvas) {
            modifier = minCanvas;
          } else if (y + height * scaleY + modifier > maxCanvas) {
            modifier = maxCanvas - height * scaleX - y;
          }
        }
      }

      if (axis === 'x') {
        if (x + modifier < minCanvas) {
          modifier = minCanvas - x;
        } else if (x + width * scaleX + modifier > maxCanvas) {
          modifier = maxCanvas - width * scaleX - x;
        }
      } else if (axis === 'y') {
        if (y + modifier < minCanvas) {
          modifier = minCanvas - y;
        } else if (y + height * scaleY + modifier > maxCanvas) {
          modifier = maxCanvas - height * scaleX - y;
        }
      }
    }
  });

  newScribe.elements.forEach((el, index) => {
    if (elementsToUpdate.includes(el.id)) {
      newScribe.elements[index][axis] = newScribe.elements[index][axis] + modifier;
    }
  });

  let firstArrowPressed = yield select(state => state.ui.firstArrowPressed);
  if (!firstArrowPressed) {
    yield put(toggleFirstArrowPress());
    yield put(updateScribeAction(newScribe));
  } else {
    yield put(updateScribeAction(newScribe, true));
  }

  delayedLastArrowCheckCall = yield delay(500);
  yield delayedLastArrowPressCheck();
}

function* delayedLastArrowPressCheck() {
  yield put(toggleFirstArrowPress());
}

function* scribeSagas() {
  yield takeLatest(ADD_SCRIBE_REQUESTED, addProject);
  yield takeEvery(CREATE_ELEMENT, createElement);
  yield takeEvery(CREATE_TEXT_ELEMENT, createTextElement);
  yield takeEvery(CREATE_SHAPE_ELEMENT, createShapeElement);
  yield takeEvery(CREATE_IMAGE_ELEMENT, createImageElement);
  yield takeEvery(CREATE_SCENE, createScene);
  yield takeLatest(REARRANGE_ELEMENTS_ON_TIMELINE, rearrangeElementsOnTimeline);
  yield takeLatest(DELETE_ACTIVE_ELEMENTS, deleteActiveElements);
  yield takeLatest(LOAD_SCRIBES, loadScribes);
  yield takeLatest(LOAD_MORE_SCRIBES, loadMoreScribes);
  yield takeLatest(LOAD_ALL_PROJECTS, loadAllProjects);
  yield takeLatest(DELETE_SCRIBE, deleteScribe);
  yield takeLatest(SORT_SCRIBES, sortScribes);
  yield takeLatest(FILTER_SCRIBES_BY_CANVAS_SIZE, handleOrientationFilter);
  yield takeLatest(DELETE_MULTIPLE_ELEMENTS_BY_ID, deleteMultipleElementsById);
  yield takeLatest(LOAD_SCRIBE_BY_ID, loadProjectById);
  yield takeLatest(LOAD_CURSORS, loadCursors);
  yield takeLatest(SET_ACTIVE_SCRIBE, prepProjectForEditor);
  yield takeLatest(UPDATE_SCENE_ELEMENTS, updateSceneElements);
  yield takeLatest(UPDATE_SCENE_THUMBNAIL, updateSceneThumbnail);
  yield takeLatest(DUPLICATE_SELECTED_SCENES, duplicateScenes);
  yield takeLatest(DELETE_SELECTED_SCENES, deleteSelectedScenes);
  yield takeLatest(UPDATE_SCENE, updateScene);
  yield takeLatest(CHANGE_SCENE_ORDER, changeSceneOrder);
  yield takeLatest(SELECT_ALL, selectAll);
  yield takeLatest(ON_MOVE_WITH_KEYS, moveElementsWithKeys);
  yield takeLatest(SET_SELECTED_AUDIO_CLIP, audioClipSelected);
  yield debounce(900, FILTER_SCRIBES_BY_SEARCH_TERM, handleSearchTermChange);
  yield fork(duplicateScribeSagas);
  yield fork(updateProjectTitleSaga);
  yield takeLatest(UPDATE_SCRIBE_FAILED, updateScribeFailed);
}

export default scribeSagas;
